import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './CookieBanner.css';

/**
 * Toggle switch component for a single cookie category.
 *
 * @param {Object} props
 * @param {string} props.id - Unique id for the checkbox input (used for label association)
 * @param {string} props.label - Display label for the category
 * @param {string} props.description - Explanatory text shown below the label
 * @param {boolean} props.checked - Current checked state
 * @param {boolean} [props.disabled=false] - Whether the toggle is disabled (necessary)
 * @param {Function} [props.onChange] - Change handler
 * @returns {React.ReactElement}
 */
function CategoryToggle({ id, label, description, checked, disabled = false, onChange }) {
  return (
    <div className="cookie-modal__category">
      <div className="cookie-modal__category-header">
        <span className="cookie-modal__category-label" id={`${id}-label`}>
          {label}
        </span>
        <label className="cookie-toggle" aria-label={label}>
          <input
            type="checkbox"
            id={id}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            aria-labelledby={`${id}-label`}
            data-category={id.replace('cookie-toggle-', '')}
          />
          <span className="cookie-toggle__track" aria-hidden="true" />
        </label>
      </div>
      <p className="cookie-modal__category-desc">{description}</p>
    </div>
  );
}

CategoryToggle.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

/**
 * Cookie preferences modal with three category toggles.
 *
 * Allows the user to independently control Necessary (always on), Analytics,
 * and Marketing cookie categories. Necessary cannot be turned off (LEGAL-09).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when the user dismisses without saving
 * @param {Function} props.onSave - Called with {necessary, analytics, marketing} when saved
 * @param {{analytics: boolean, marketing: boolean}} [props.initialPrefs] - Initial toggle state
 * @returns {React.ReactElement|null}
 *
 * @example
 * <CookiePreferencesModal
 *   isOpen={modalOpen}
 *   onClose={() => setModalOpen(false)}
 *   onSave={(prefs) => handleSave(prefs)}
 *   initialPrefs={{ analytics: false, marketing: false }}
 * />
 */
function CookiePreferencesModal({ isOpen, onClose, onSave, initialPrefs = {} }) {
  const [analytics, setAnalytics] = useState(Boolean(initialPrefs.analytics));
  const [marketing, setMarketing] = useState(Boolean(initialPrefs.marketing));
  const headingId = 'cookie-prefs-heading';
  const headingRef = useRef(null);

  // Sync state when initialPrefs changes (e.g. re-open after prior save)
  useEffect(() => {
    setAnalytics(Boolean(initialPrefs.analytics));
    setMarketing(Boolean(initialPrefs.marketing));
  }, [initialPrefs.analytics, initialPrefs.marketing]);

  // Move focus to heading when modal opens (accessibility)
  useEffect(() => {
    if (isOpen && headingRef.current) {
      headingRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  /**
   * Handle overlay click — close modal when clicking outside the dialog card.
   * @param {React.SyntheticEvent} e
   */
  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  /**
   * Handle Save button click — call onSave with current preferences.
   */
  function handleSave() {
    onSave({
      necessary: true,
      analytics,
      marketing,
    });
  }

  return (
    <div
      className="cookie-modal__overlay"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="cookie-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="cookie-modal__header">
          <h2
            id={headingId}
            className="cookie-modal__title"
            ref={headingRef}
            tabIndex={-1}
          >
            Manage Cookie Preferences
          </h2>
          <p className="cookie-modal__subtitle">
            Choose which cookies you allow. Necessary cookies are required for the
            site to function and cannot be disabled.
          </p>
        </div>

        {/* Category toggles */}
        <CategoryToggle
          id="cookie-toggle-necessary"
          label="Necessary"
          description="These cookies are essential for the website to function. They enable core features like account login, security, and session management."
          checked={true}
          disabled={true}
        />

        <CategoryToggle
          id="cookie-toggle-analytics"
          label="Analytics"
          description="These cookies help us understand how visitors use the site by collecting anonymized usage data. We use this to improve the product."
          checked={analytics}
          onChange={(e) => setAnalytics(e.target.checked)}
        />

        <CategoryToggle
          id="cookie-toggle-marketing"
          label="Marketing"
          description="These cookies are used to deliver relevant advertising and track campaign effectiveness across websites."
          checked={marketing}
          onChange={(e) => setMarketing(e.target.checked)}
        />

        {/* Footer */}
        <div className="cookie-modal__footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

CookiePreferencesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialPrefs: PropTypes.shape({
    analytics: PropTypes.bool,
    marketing: PropTypes.bool,
  }),
};

export default CookiePreferencesModal;
